import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { UserPreferences } from '@shared/types';

interface PreferencesContextType {
  preferences: UserPreferences;
  updatePreferences: (updates: Partial<UserPreferences>) => void;
  toggleFocusMode: () => void;
}

const defaultPreferences: UserPreferences = {
  theme: 'system',
  reducedMotion: false,
  focusMode: false,
  notificationSound: true,
  defaultView: 'dashboard',
  compactMode: false,
};

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(() => {
    if (typeof window === 'undefined') return defaultPreferences;

    const stored = localStorage.getItem('wez-preferences');
    if (stored) {
      try {
        return { ...defaultPreferences, ...JSON.parse(stored) };
      } catch {
        return defaultPreferences;
      }
    }

    // Check system preference for reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    return {
      ...defaultPreferences,
      reducedMotion: prefersReducedMotion,
    };
  });

  useEffect(() => {
    localStorage.setItem('wez-preferences', JSON.stringify(preferences));
  }, [preferences]);

  function updatePreferences(updates: Partial<UserPreferences>) {
    setPreferences((prev) => ({ ...prev, ...updates }));
  }

  function toggleFocusMode() {
    setPreferences((prev) => ({ ...prev, focusMode: !prev.focusMode }));
  }

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        updatePreferences,
        toggleFocusMode,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}
